import { ReactElement, useMemo } from "react";
import Script from "next/script";

interface CakeProps {
    cakeClickPixelUrl: string;
    cakeRequestIdFromCookie?: number;
}

/**
 * Cake Marketing script component that handles tracking pixel integration.
 * Only renders when no existing utm_click_id is present in URL or Cookie.
 * The script sets `window.ckm_request_id` as a global variable.
 */
export const Cake = ({
    cakeClickPixelUrl,
    cakeRequestIdFromCookie,
}: CakeProps): ReactElement | null => {
    const utmClickIdIsPresent = useMemo(
        () => isUtmClickIdPresent(cakeRequestIdFromCookie),
        [cakeRequestIdFromCookie]
    );

    if (!cakeClickPixelUrl || utmClickIdIsPresent) {
        return null;
    }

    return (
        <Script
            id="cakeClickPixelUrl"
            src={cakeClickPixelUrl}
            strategy="afterInteractive"
        />
    );
};

/**
 * Checks if a UTM click ID is present either in the URL parameters or cookie
 * @param cakeRequestIdFromCookie - The cake request ID stored in cookie
 * @returns boolean indicating if a UTM click ID is present
 */
const isUtmClickIdPresent = (cakeRequestIdFromCookie?: number): boolean => {
    if (typeof window === 'undefined') return false;

    try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlUtmClickId = urlParams
            .getAll("utm_click_id")
            .find((element) => Boolean(element));

        return Boolean(urlUtmClickId || cakeRequestIdFromCookie);
    } catch (error) {
        console.error('Error checking UTM click ID:', error);
        return false;
    }
};

export const exportedForTesting = { isUtmClickIdPresent };
